import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AdvancedRole,
  InstrumentItem,
  InstrumentOption,
  InstrumentPayload,
  InstrumentSection,
  getAdvancedInstrument,
  saveAdvancedAnswer,
} from './advancedAssessmentApi';

/**
 * The Advanced questionnaire.
 *
 * Three decisions here come from evidence rather than taste:
 *
 * - Forced-choice inventories render as sequential Yes / No / Not sure rows, never a grid.
 *   Grids are answered roughly 15x faster than the same items presented one at a time, which
 *   is straight-lining rather than reading, and checklists under-report by ~8 points against
 *   forced choice on exactly this kind of inventory.
 * - "Not sure" is offered wherever honest ignorance is possible. Removing it converts
 *   ignorance into a false assertion, which is worse for the matcher than a lower confidence.
 * - Every answer autosaves server-side on change. Nothing is scored in the browser.
 */

type AnswerValue = string | Record<string, boolean | null> | undefined;
type Answers = Record<string, AnswerValue>;

const Shell = styled.div`
  max-width: 46rem;
  margin: 0 auto;
  padding: 1.5rem 1rem 4rem;
  h2, input, textarea, fieldset { scroll-margin-top: 8rem; }
`;

const Progress = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  list-style: none;
  margin: 0 0 1.75rem;
  padding: 0;
`;

const Step = styled.li<{ $state: 'done' | 'active' | 'todo' }>`
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.3rem 0.6rem;
  border-radius: 2px;
  background: ${({ $state }) =>
    $state === 'active' ? 'var(--color-accent-primary-fill, #0A7DAA)' : '#F0F0F0'};
  color: ${({ $state }) => ($state === 'active' ? '#fff' : '#494949')};
  opacity: ${({ $state }) => ($state === 'todo' ? 0.6 : 1)};
`;

const Group = styled.fieldset`
  border: 0;
  border-top: 1px solid #DDDDDD;
  margin: 0 0 1.75rem;
  padding: 1.25rem 0 0;

  legend {
    padding: 0;
    font-weight: 600;
    font-size: 1rem;
    color: #143562;
  }
`;

const Guidance = styled.p`
  margin: 0.3rem 0 0.75rem;
  font-size: 0.875rem;
  color: #494949;
`;

const Choice = styled.label<{ $selected: boolean }>`
  text-transform: none; letter-spacing: normal;
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.4rem;
  border: 1px solid ${({ $selected }) => ($selected ? '#0A7DAA' : '#DDDDDD')};
  background: ${({ $selected }) => ($selected ? 'rgba(10,125,170,0.06)' : '#fff')};
  cursor: pointer;
  min-height: 44px;

  input { margin-top: 0.2rem; }
  span.label { font-size: 0.9375rem; }
  span.desc { display: block; font-size: 0.8125rem; color: #494949; }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #F0F0F0;

  > span { font-size: 0.9375rem; }
  > div { display: flex; gap: 0.3rem; flex-shrink: 0; }
  @media (max-width: 520px) { align-items: flex-start; flex-direction: column; gap: 0.5rem; }
`;

const RowChoice = styled.label<{ $selected: boolean }>`
  text-transform: none; letter-spacing: normal;
  font-size: 0.8125rem;
  padding: 0.35rem 0.6rem;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $selected }) => ($selected ? '#0A7DAA' : '#DDDDDD')};
  background: ${({ $selected }) => ($selected ? '#0A7DAA' : '#fff')};
  color: ${({ $selected }) => ($selected ? '#fff' : '#494949')};
  cursor: pointer;

  &:focus-within { outline: 3px solid #143562; outline-offset: 2px; }
  input { position: absolute; opacity: 0; width: 1px; height: 1px; }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.7rem;
  border: 1px solid #DDDDDD;
  font: inherit;
  min-height: 44px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 9rem;
  padding: 0.7rem;
  border: 1px solid #DDDDDD;
  font: inherit;
  line-height: 1.55;
  resize: vertical;
`;

/* State is carried by the text, never by colour alone: the brand has no semantic warning
   tone, and colour-only encoding fails the accessibility bar this report is held to. */
const WordCount = styled.p<{ $ok: boolean }>`
  margin: 0.3rem 0 0;
  font-size: 0.8125rem;
  color: #494949;
  font-weight: ${({ $ok }) => ($ok ? 400 : 600)};
`;

const SaveState = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: #494949;
  min-height: 1.2rem;
`;

const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.7rem 1.2rem;
  min-height: 44px;
  border: 1px solid #0A7DAA;
  background: #0A7DAA;
  color: #fff;
  font: inherit;
  cursor: pointer;

  &[data-variant='quiet'] { background: #fff; color: #0A7DAA; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

/** Options narrowed to what the respondent actually said yes to on a prior forced-choice item.
 *  Asking "which of these matters most?" and then listing the ones they excluded is the fastest
 *  way to tell someone the form is not listening. */
export const visibleOptions = (
  item: InstrumentItem,
  answers: Answers
): InstrumentOption[] => {
  const all = item.options ?? [];
  if (!item.optionsFrom) return all;
  const source = answers[item.optionsFrom];
  if (typeof source !== 'object' || source === null) return all;
  const affirmative = Object.entries(source as Record<string, boolean | null>)
    .filter(([, value]) => value === true)
    .map(([key]) => key);
  if (!affirmative.length) return all;
  return all.filter((option) => affirmative.includes(option.value) || option.unknown);
};

/** An item renders only when its gate is satisfied; a hidden item is never a non-response. */
export const isVisible = (item: InstrumentItem, answers: Answers): boolean => {
  if (!item.shownWhen) return true;
  const gate = answers[item.shownWhen.item];
  if (typeof gate !== 'string') return false;
  if (item.shownWhen.values) return item.shownWhen.values.includes(gate);
  return gate === item.shownWhen.value;
};

export const answerComplete = (item: InstrumentItem, answers: Answers): boolean => {
  const value = answers[item.id];
  if (value === undefined || value === '') return !item.required;
  if (item.format === 'forced_choice') return typeof value === 'object' &&
    (item.options ?? []).every(option => Object.prototype.hasOwnProperty.call(value, option.value));
  if (item.format === 'single_choice' || item.format === 'true_false') return visibleOptions(item, answers).some(option => option.value === value);
  if (typeof value !== 'string' || !value.trim()) return !item.required;
  const words = countWords(value);
  return (!item.minWords || words >= item.minWords) && (!item.maxWords || words <= item.maxWords);
};

const displayAnswer = (item: InstrumentItem, value: AnswerValue): string => {
  if (value === undefined || value === '') return 'Not provided (optional)';
  if (typeof value === 'object') return (item.options ?? []).map(option =>
    `${option.label}: ${value[option.value] === true ? 'Yes' : value[option.value] === false ? 'No' : 'Not sure'}`
  ).join('; ');
  return item.options?.find(option => option.value === value)?.label ?? value;
};

interface Props {
  sessionId: string;
  accessToken: string;
  respondent?: 'exec' | 'it';
  onComplete?: () => Promise<void> | void;
}

const AdvancedQuestionnaire: React.FC<Props> = ({
  sessionId, accessToken, respondent = 'exec', onComplete,
}) => {
  const [instrument, setInstrument] = useState<InstrumentPayload | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [sectionIndex, setSectionIndex] = useState(0);
  const [saveState, setSaveState] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [paused, setPaused] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const dirty = useRef(new Map<string, { item: InstrumentItem; value: AnswerValue }>());
  const saving = useRef<Promise<boolean> | null>(null);
  const mounted = useRef(true);
  const draftKey = `davinci-assessment-draft-${sessionId}-${respondent}`;
  const retainPending = useCallback(() => {
    try {
      if (dirty.current.size) sessionStorage.setItem(draftKey, JSON.stringify(
        Object.fromEntries(Array.from(dirty.current.entries()).map(([key, entry]) => [key, entry.value]))
      ));
      else sessionStorage.removeItem(draftKey);
    } catch { /* beforeunload still protects changes when storage is unavailable. */ }
  }, [draftKey]);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    getAdvancedInstrument(sessionId, accessToken, respondent)
      .then((payload) => {
        if (cancelled) return;
        setInstrument(payload);
        const restored = { ...(payload.answers ?? {}) };
        try {
          const pending = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
          if (!payload.submitted) payload.sections.forEach(section => section.items.forEach(item => {
            if (Object.prototype.hasOwnProperty.call(pending, item.id)) {
              restored[item.id] = pending[item.id];
              dirty.current.set(item.id, { item, value: pending[item.id] });
            }
          }));
        } catch { /* A malformed local draft never prevents server recovery. */ }
        setAnswers(restored);
        if (dirty.current.size) setSaveState('Restored changes waiting to save');
      })
      .catch((loadError: Error) => { if (!cancelled) setError(loadError.message); });
    return () => { cancelled = true; mounted.current = false; };
  }, [sessionId, accessToken, respondent, draftKey]);

  // One queue owns all writes. Changes during a request remain queued, so a slow earlier
  // response can never overwrite the latest edit or mark a newer edit as saved.
  const flush = useCallback((): Promise<boolean> => {
    if (timer.current) clearTimeout(timer.current);
    if (saving.current) return saving.current;
    const work = async () => {
      try {
        while (dirty.current.size) {
          const [key, entry] = Array.from(dirty.current.entries())[0];
          if (mounted.current) setSaveState('Saving…');
          await saveAdvancedAnswer(sessionId, accessToken, {
            role: respondent as AdvancedRole, questionKey: entry.item.id, draft: true,
            ...(entry.item.format === 'long_answer' || entry.item.format === 'short_answer'
              ? { response: String(entry.value ?? '') }
              : { answer: entry.value as string | Record<string, boolean | null> }),
          });
          if (dirty.current.get(key) === entry) dirty.current.delete(key);
          retainPending();
        }
        if (mounted.current) { setSaveState('All answers saved'); setError(''); }
        return true;
      } catch (saveError) {
        if (mounted.current) {
          setSaveState('Changes waiting to save');
          setError(`${(saveError as Error).message} Keep this page open and retry saving.`);
        }
        return false;
      } finally { saving.current = null; }
    };
    if (!dirty.current.size) return Promise.resolve(true);
    saving.current = work();
    return saving.current;
  }, [sessionId, accessToken, respondent, retainPending]);

  const update = useCallback((item: InstrumentItem, value: AnswerValue, debounceMs = 500) => {
    setAnswers(current => ({ ...current, [item.id]: value }));
    dirty.current.set(item.id, { item, value });
    retainPending();
    setSaveState('Changes waiting to save');
    setConfirmed(false);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, debounceMs);
  }, [flush, retainPending]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty.current.size) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (timer.current) clearTimeout(timer.current);
      void flush();
    };
  }, [flush]);

  useEffect(() => { heading.current?.focus(); }, [sectionIndex, reviewing, paused]);

  const sections: InstrumentSection[] = instrument?.sections ?? [];
  const section = sections[sectionIndex];
  const visibleItems = useMemo(
    () => section ? section.items.filter(item => isVisible(item, answers)) : [],
    [section, answers]
  );
  const outstanding = visibleItems.filter(item => !answerComplete(item, answers));

  const advance = async () => {
    setBusy(true);
    try {
      if (!await flush()) return;
      const refreshed = await getAdvancedInstrument(sessionId, accessToken, respondent);
      setInstrument(refreshed);
      const index = refreshed.sections.findIndex(entry => entry.id === section.id);
      if (index + 1 < refreshed.sections.length) setSectionIndex(index + 1);
      else setReviewing(true);
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try { if (await flush()) await onComplete?.(); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  if (error && !instrument) return <Shell><p role="alert">{error}</p><Button onClick={() => window.location.reload()}>Retry loading</Button></Shell>;
  if (!instrument || !section) return <Shell><p>Loading your saved answers…</p></Shell>;
  if (paused) return <Shell>
    <h2 ref={heading} tabIndex={-1}>Your progress is saved.</h2>
    <p>You can take a break. Keep this tab to continue here, or request the private recovery link below before closing it or switching devices.</p>
    <Button onClick={() => setPaused(false)}>Continue assessment</Button>
  </Shell>;
  if (reviewing) return <Shell>
    <h2 ref={heading} tabIndex={-1}>Review before submitting</h2>
    <p>Your report will use these answers to explain priorities, assumptions, costs and next steps. Check anything that may change the recommendation.</p>
    {sections.map((entry, index) => <details key={entry.id}>
      <summary>{entry.label}</summary>
      <dl>{entry.items.filter(item => isVisible(item, answers)).map(item => <React.Fragment key={item.id}>
        <dt>{item.prompt}</dt><dd>{displayAnswer(item, answers[item.id])}</dd>
      </React.Fragment>)}</dl>
      <Button data-variant="quiet" onClick={() => { setReviewing(false); setConfirmed(false); setSectionIndex(index); }}>Edit {entry.label}</Button>
    </details>)}
    <Choice $selected={confirmed}><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
      <span>I have reviewed my answers and am ready for Davinci to prepare my report. Changes after submission require support.</span>
    </Choice>
    {error && <p role="alert">{error}</p>}
    <Button disabled={!confirmed || busy || sections.some(entry => entry.items.some(item => isVisible(item, answers) && !answerComplete(item, answers)))} onClick={submit}>
      {busy ? 'Submitting…' : 'Submit and prepare my report'}
    </Button>
  </Shell>;

  const renderItem = (item: InstrumentItem) => {
    const value = answers[item.id];

    if (item.format === 'single_choice' || item.format === 'true_false') {
      return (
        <Group key={item.id}>
          <legend>{item.prompt}</legend>
          {item.guidance && <Guidance>{item.guidance}</Guidance>}
          {visibleOptions(item, answers).map((option) => (
            <Choice key={option.value} $selected={value === option.value}>
              <input
                type="radio"
                name={item.id}
                value={option.value}
                checked={value === option.value}
                onChange={() => update(item, option.value)}
              />
              <span>
                <span className="label">{option.label}</span>
                {option.description && <span className="desc">{option.description}</span>}
              </span>
            </Choice>
          ))}
        </Group>
      );
    }

    if (item.format === 'forced_choice') {
      const rows = (value as Record<string, boolean | null>) ?? {};
      // Debounced: a respondent working down a twelve-row inventory would otherwise post
      // twelve times, and the whole item map is sent on each change anyway.
      const setRow = (rowValue: string, next: boolean | null) =>
        update(item, { ...rows, [rowValue]: next }, 700);
      return (
        <Group key={item.id}>
          <legend>{item.prompt}</legend>
          {item.guidance && <Guidance>{item.guidance}</Guidance>}
          {(item.options ?? []).map((option) => (
            <Row key={option.value}>
              <span id={`${item.id}-${option.value}`}>{option.label}</span>
              <div role="group" aria-labelledby={`${item.id}-${option.value}`}>
                {([['Yes', true], ['No', false], ['Not sure', null]] as const).map(
                  ([label, next]) => (
                    <RowChoice key={label} $selected={rows[option.value] === next}>
                      <input
                        type="radio"
                        name={`${item.id}-${option.value}`}
                        checked={rows[option.value] === next}
                        onChange={() => setRow(option.value, next)}
                      />
                      {label}
                    </RowChoice>
                  )
                )}
              </div>
            </Row>
          ))}
        </Group>
      );
    }

    if (item.format === 'short_answer') {
      return (
        <Group key={item.id}>
          <legend><label htmlFor={item.id}>{item.prompt}</label></legend>
          {item.guidance && <Guidance>{item.guidance}</Guidance>}
          <TextInput
            id={item.id}
            maxLength={item.maxLength ?? 200}
            value={(value as string) ?? ''}
            onChange={(event) => update(item, event.target.value, 900)}
          />
        </Group>
      );
    }

    const text = (value as string) ?? '';
    const words = countWords(text);
    const withinRange =
      words === 0 || ((!item.minWords || words >= item.minWords) &&
                      (!item.maxWords || words <= item.maxWords));
    return (
      <Group key={item.id}>
        <legend><label htmlFor={item.id}>{item.prompt}</label></legend>
        {item.guidance && <Guidance>{item.guidance}</Guidance>}
        <TextArea
          id={item.id}
          value={text}
          onChange={(event) => update(item, event.target.value, 1200)}
        />
        <WordCount $ok={withinRange} aria-live="polite">
          {words} words
          {item.minWords ? ` · aim for ${item.minWords}–${item.maxWords}` : ''}
          {!withinRange && words > 0 ? ' · outside the suggested range' : ''}
        </WordCount>
      </Group>
    );
  };

  const last = sectionIndex === sections.length - 1;

  return (
    <Shell data-clarity-mask="True">
      <Progress aria-label="Assessment progress">
        {sections.map((entry, index) => (
          <Step
            key={entry.id}
            $state={index < sectionIndex ? 'done' : index === sectionIndex ? 'active' : 'todo'}
            aria-current={index === sectionIndex ? 'step' : undefined}
          >
            {entry.label}
          </Step>
        ))}
      </Progress>

      <h2 ref={heading} tabIndex={-1}>{section.label}</h2>
      <p>Section {sectionIndex + 1} of {sections.length}. Your answers save as you go. Choose “Not sure” when you need someone else to verify a detail.</p>
      {section.id.startsWith('DD') && instrument.shortlist && <p>These follow-up questions focus on the areas your earlier answers raised: {instrument.shortlist.map(area => area.label).join(', ')}.</p>}
      <fieldset disabled={busy} style={{ border: 0, padding: 0, minWidth: 0 }}>
      {visibleItems.map(renderItem)}

      </fieldset>
      <SaveState aria-live="polite">{saveState}</SaveState>
      {error && <p role="alert">{error} <Button onClick={() => { void flush(); }}>Retry saving</Button></p>}
      <Button type="button" data-variant="quiet" disabled={busy} onClick={async () => {
        setBusy(true); if (await flush()) setPaused(true); setBusy(false);
      }}>Save and take a break</Button>

      <Nav>
        <Button
          type="button"
          data-variant="quiet"
          disabled={sectionIndex === 0 || busy}
          onClick={async () => { setBusy(true); if (await flush()) setSectionIndex(index => Math.max(0, index - 1)); setBusy(false); }}
        >
          Back
        </Button>
        <Button
          type="button"
          disabled={outstanding.length > 0 || busy}
          onClick={advance}
        >
          {busy ? 'Saving…' : last ? 'Review answers and next steps' : `Continue${outstanding.length ? ` (${outstanding.length} left)` : ''}`}
        </Button>
      </Nav>
    </Shell>
  );
};

export default AdvancedQuestionnaire;
